import React, { useState, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import '../styles/LanguageSelector.css';

interface Language {
  code: string;
  name: string;
  flag: string;
  visible?: boolean;
}

const FALLBACK_LANGUAGES: Language[] = [
  { code: 'en', name: 'English', flag: '🇬🇧' },
  { code: 'es', name: 'Spanish', flag: '🇪🇸' },
  { code: 'fr', name: 'French', flag: '🇫🇷' },
  { code: 'de', name: 'German', flag: '🇩🇪' },
  { code: 'it', name: 'Italian', flag: '🇮🇹' },
  { code: 'pt', name: 'Portuguese', flag: '🇵🇹' },
  { code: 'sv', name: 'Swedish', flag: '🇸🇪' },
  { code: 'no', name: 'Norwegian', flag: '🇳🇴' },
  { code: 'da', name: 'Danish', flag: '🇩🇰' },
  { code: 'fi', name: 'Finnish', flag: '🇫🇮' },
  { code: 'ja', name: 'Japanese', flag: '🇯🇵' },
  { code: 'zh', name: 'Chinese', flag: '🇨🇳' }
];

const FLAG_BY_CODE: Record<string, string> = {
  en: '🇬🇧',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
  it: '🇮🇹',
  pt: '🇵🇹',
  sv: '🇸🇪',
  no: '🇳🇴',
  da: '🇩🇰',
  fi: '🇫🇮',
  ja: '🇯🇵',
  zh: '🇨🇳'
};

const resolveFlag = (lang: Language) =>
  lang.flag || FLAG_BY_CODE[lang.code] || '🏳️';

const LANGUAGE_STORAGE_KEY = 'hypedirector-landing-locale';

export const LanguageSelector: React.FC = () => {
  const intl = useIntl();
  const [isOpen, setIsOpen] = useState(false);
  const [currentLocale, setCurrentLocale] = useState<string>(intl.locale || 'en');
  const [languages, setLanguages] = useState<Language[]>(FALLBACK_LANGUAGES);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    // Load saved language preference from localStorage
    const savedLocale = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    if (savedLocale && languages.some(lang => lang.code === savedLocale)) {
      setCurrentLocale(savedLocale);
      if (savedLocale !== intl.locale) {
        // Trigger language change by reloading the page with new locale
        window.location.reload();
      }
    }
  }, [intl.locale, languages]);

  useEffect(() => {
    const sorted = [...FALLBACK_LANGUAGES].sort((a, b) => a.name.localeCompare(b.name));
    setLanguages(sorted);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
        buttonRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen]);

  const currentLanguage = languages.find(lang => lang.code === currentLocale) || FALLBACK_LANGUAGES[0];
  const currentFlag = resolveFlag(currentLanguage);

  const handleLanguageSelect = (langCode: string) => {
    if (langCode !== currentLocale) {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, langCode);
      window.location.reload();
    }
    setIsOpen(false);
  };

  return (
    <div className="language-selector-wrapper">
      <button
        ref={buttonRef}
        className="language-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label={intl.formatMessage({ id: 'landing.language.select' })}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
      >
        <span className="language-flag">{currentFlag}</span>
        <span className="language-name">{currentLanguage.name}</span>
        <span className={`language-arrow ${isOpen ? 'open' : ''}`}>▼</span>
      </button>

      {isOpen && (
        <div className="language-dropdown" ref={dropdownRef} role="listbox">
          {languages.map((lang) => (
            <button
              key={lang.code}
              className={`language-option ${lang.code === currentLocale ? 'selected' : ''}`}
              onClick={() => handleLanguageSelect(lang.code)}
              role="option"
              aria-selected={lang.code === currentLocale}
            >
              <span className="language-option-flag">{resolveFlag(lang)}</span>
              <span className="language-option-name">{lang.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
